import { useEffect, useRef, useState, ReactNode } from 'react';

interface Props {
  children: ReactNode;
  className?: string;
  delayMs?: number;
  direction?: 'up' | 'fade' | 'left' | 'right';
}

export default function RevealOnScroll({
  children,
  className = '',
  delayMs = 0,
  direction = 'up',
}: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          obs.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const hiddenClass = {
    up: 'opacity-0 translate-y-6',
    fade: 'opacity-0',
    left: 'opacity-0 -translate-x-6',
    right: 'opacity-0 translate-x-6',
  }[direction];

  const visibleClass = {
    up: 'opacity-100 translate-y-0',
    fade: 'opacity-100',
    left: 'opacity-100 translate-x-0',
    right: 'opacity-100 translate-x-0',
  }[direction];

  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ease-out ${visible ? visibleClass : hiddenClass} ${className}`}
      style={{ transitionDelay: visible ? `${delayMs}ms` : '0ms' }}
    >
      {children}
    </div>
  );
}
